import { add, isBefore, parse, sub } from "date-fns";
import React, { useState } from "react";
import { useWindowDimensions } from "react-native";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";
import { getAllDatesBetweenTwoDays } from "@whoosh/utilities/src/dates";

import { CalendarMonth } from "../../Calendar";

export const TwoCalendarMonths = ({
  curStartDate,
  curEndDate,
  handleUpdateRange,
  clubWeekStartsOn,
}: {
  curStartDate: string;
  curEndDate: string;
  handleUpdateRange: (startDate: string, endDate: string) => void;
  clubWeekStartsOn: NumericalDayOfWeek;
}) => {
  const [selectedDates, setSelectedDates] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: curStartDate,
    endDate: curEndDate,
  });

  const selectedDatesArray = [
    selectedDates.startDate ?? "",
    selectedDates.endDate ?? "",
  ];

  const highlightedDatesArray =
    selectedDates.startDate && selectedDates.endDate
      ? getAllDatesBetweenTwoDays(
          selectedDates.startDate,
          selectedDates.endDate
        )
      : [];

  const styledDates = {
    selectedDates: selectedDatesArray,
    highlightedDates: highlightedDatesArray,
  };

  const [firstMonthObject, setFirstMonthObject] = useState(
    parse(curStartDate, "yyyy-MM-dd", new Date())
  );
  const nextMonthObj = add(firstMonthObject, { months: 1 });

  const decrementMonths = () => {
    const preMonth = sub(firstMonthObject, { months: 1 });
    setFirstMonthObject(preMonth);
  };

  const incrementMonths = () => {
    const nextMonth = add(firstMonthObject, { months: 1 });
    setFirstMonthObject(nextMonth);
  };

  const handleDatePress = (date: string) => {
    const hasBothDates = selectedDates.startDate && selectedDates.endDate;
    const isBeforeStartDate =
      selectedDates.startDate &&
      isBefore(
        parse(date, "yyyy-MM-dd", new Date()),
        parse(selectedDates.startDate, "yyyy-MM-dd", new Date())
      );

    if (!selectedDates.startDate || hasBothDates || isBeforeStartDate) {
      setSelectedDates({ startDate: date, endDate: null });
    } else {
      setSelectedDates({ ...selectedDates, endDate: date });
      handleUpdateRange(selectedDates.startDate, date);
    }
  };

  const { width } = useWindowDimensions();
  const showTwoCalendars = width >= 1150;

  /**
   * Leave the min height so that iOS will display correctly
   */
  return (
    <HStack position="relative" minHeight={350} bg="grayscale-0">
      <Box position="absolute" left={65} zIndex={10000}>
        <Pressable onPress={decrementMonths}>
          <ChevronLeftIcon />
        </Pressable>
      </Box>
      <Box position="absolute" right={65} zIndex={10000}>
        <Pressable onPress={incrementMonths}>
          <ChevronRightIcon />
        </Pressable>
      </Box>

      <Box mr={showTwoCalendars ? 8 : 0} flex={1}>
        <CalendarMonth
          styledDates={styledDates}
          monthDateObj={firstMonthObject}
          onDatePress={handleDatePress}
          clubWeekStartsOn={clubWeekStartsOn}
        />
      </Box>
      {showTwoCalendars ? (
        <CalendarMonth
          styledDates={styledDates}
          monthDateObj={nextMonthObj}
          onDatePress={handleDatePress}
          clubWeekStartsOn={clubWeekStartsOn}
        />
      ) : null}
    </HStack>
  );
};
